import {
  DiscoveryAssistantMode,
  type DiscoveryMessage,
  type DiscoverySession,
} from '@/entities/discovery';

import { cn } from '@/shared/utils';
import { MousePointerClick, PencilLine } from 'lucide-react';
import { ToggleStage } from '@/shared/components/stage/toggle-stage';
import { Button } from '@/shared/components/shadcn/button';
import { DiscoveryMessageMap } from '../ui/discovery-message';
import { useDiscoveryStore } from '../stores/discovery-store';
import { ChatPanelInput } from '../ui/discovery-prompt-input/chat-panel-input';
import { requestConfirm } from '@/shared/utils/request-confirm';
import { useDiscoveryMessagesQuery } from '../stores/discovery-message-store';
import { useExecuteLatestDiscoverySearchMessage } from '../hooks/use-execute-latest-discovery-search-message';
import { useDiscoveryMessageAutoScroll } from '../hooks/use-discovery-message-auto-scroll';

export const DiscoveryChatPanel = ({ session }: { session: DiscoverySession }) => {
  const { resetSession } = useDiscoveryStore();

  const { messages } = useDiscoveryMessagesQuery(session.session_id);
  const currentSearchMessageId = getCurrentSearchMessageId(messages);
  useExecuteLatestDiscoverySearchMessage({ messages, session });

  const latestMessageId = messages[0]?.message_id;
  const scrollContainerRef = useDiscoveryMessageAutoScroll(latestMessageId);

  return (
    <aside
      className={cn(
        'bg-background border-border flex h-full min-w-0 flex-col overflow-hidden border-r',
      )}
    >
      <div className="flex min-h-0 w-full flex-1 flex-col">
        <div className="bg-background/85 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
          <div className="flex w-full items-center justify-start gap-1.5 px-4 py-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                requestConfirm({
                  title: 'Start a new discovery?',
                  description: 'This will clear the current chat and results',
                  onConfirm: () => {
                    void resetSession();
                  },
                  confirmButton: {
                    children: 'Start new',
                    variant: 'destructive',
                  },
                });
              }}
            >
              <PencilLine className="size-4" />
              <span>New discovery</span>
            </Button>
          </div>
        </div>
        <div
          ref={scrollContainerRef}
          className="scrollbar-hide flex min-h-0 flex-1 justify-center overflow-y-auto"
        >
          <div className="flex min-h-full w-full max-w-3xl flex-col-reverse justify-end gap-6 p-5">
            <div aria-hidden="true" className="h-2 shrink-0" />
            {messages.map((message, index) => (
              <DiscoveryMessageMap
                message={message}
                nextMessage={messages[index - 1]}
                isCurrent={message.message_id === currentSearchMessageId}
                key={message.message_id}
              />
            ))}
          </div>
        </div>

        <div className="bg-background flex justify-center p-3 pt-0">
          <div className="w-full max-w-3xl">
            <ToggleStage
              status={!!composerTopContent}
              className="bg-muted -mb-3 rounded-t-2xl px-4 pt-1 pb-4"
            >
              {composerTopContent}
            </ToggleStage>
            <div className="relative z-10">
              <ChatPanelInput sessionId={session.session_id} />
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

const composerTopContent = (
  <div className="text-muted-foreground flex min-h-7 min-w-0 items-center gap-2">
    <MousePointerClick className="size-3.5 shrink-0" />
    <p className="-mb-0.5 truncate text-sm leading-5 font-normal">
      Select posts to find lookalike creators
    </p>
  </div>
);

function getCurrentSearchMessageId(messages: DiscoveryMessage[]) {
  return (
    messages.reduce<{ messageId: string; createdAt: string } | null>((currentSearch, message) => {
      if (
        message.role !== 'assistant' ||
        message.assistant_mode !== DiscoveryAssistantMode.SEARCH
      ) {
        return currentSearch;
      }

      if (currentSearch == null || message.created_at > currentSearch.createdAt) {
        return {
          messageId: message.message_id,
          createdAt: message.created_at,
        };
      }

      return currentSearch;
    }, null)?.messageId ?? null
  );
}
